import React, { useState, useEffect, useContext } from 'react';
import UserDetailsContext from '../context/userDetails.context';
import LocationsContext from '../context/locations.Context';
import ThemeContext from '../context/theme.Context';
import LoadingContext from '../context/loading.context';
import NotificationContext from '../context/notification.Context';
import Spinner from './Spinner.component';
import { Redirect } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Table from 'react-bootstrap/Table';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import UserInvitationDialog from './UserInvitationDialog';
import ShareInvitationDialog from './ShareInvitationDialog';
import * as Constants from '../modules/constants';



function ManageUsers(props) {

    const { locations } = useContext(LocationsContext);
    const { showLoading, hideLoading } = useContext(LoadingContext);
    const { token } = useContext(UserDetailsContext);
    const { showMessage } = useContext(NotificationContext);

    const [showDialog, setShowDialog] = useState(false);
    const [showShareDialog, setShowShareDialog] = useState(false);
    const [invitations, setInvitations] = useState({});
    const [users, setUsers] = useState({});
    const [selectedInvitationUrl, setSelectedInvitationUrl] = useState(null);
    const [selectedEmail, setSelectedEmail] = useState(null);

    const apiHostName = Constants.API_URL;

    useEffect(() => {
        // Initialize pending invitations:
        showLoading();
        fetch(apiHostName + '/v1/invitations/location/' + props.location.state.locationId, {
            mode: "cors",
            method: 'GET',
            cache: "no-cache",
            headers: { "Authorization": "Bearer " + token }
        })
            .then(function (response) {
                if (!response.ok) {
                    throw new Error('Network error');
                }
                return response.json();
            })
            .then((result) => {
                // Result contains invitations (if any)
                setInvitations(result);
                hideLoading();
            })
            .catch(function (error) {
                // Set visual cue and error text to be displayed on registration screen
                console.error("Cannot retrieve invitations...");
                console.error(error);
                showMessage({ newMessage: 'Network error.' });
                hideLoading();
            });

        // Also initialize authorized users:
        showLoading();
        fetch(apiHostName + '/v1/users/location/' + props.location.state.locationId, {
            mode: "cors",
            method: 'GET',
            cache: "no-cache",
            headers: { "Authorization": "Bearer " + token }
        })
            .then(function (response) {
                if (!response.ok) {
                    throw new Error('Network error');
                }
                return response.json();
            })
            .then((result) => {
                // Result contains users for this location (if any)
                setUsers(result);
                hideLoading();
            })
            .catch(function (error) {
                // Set visual cue and error text to be displayed on registration screen
                console.error("Cannot retrieve users for location...");
                console.error(error);
                showMessage({ newMessage: 'Network error.' });
                hideLoading();
            });

    }, [props.location.state.locationId, apiHostName, token, showLoading, hideLoading, showMessage]);

    function changeRole(user) {
        const newRole = user.authorization === 'Operate' ? 'Manage' : 'Operate';
        if (!window.confirm('Are you sure you want to change the role for user ' + user.email + '?\nThe new role will be: ' + newRole))
            return;

        // Ask server to change role for user at this location:
        showLoading();
        fetch(apiHostName + '/v1/users/' + user.id + '/role?role=' + encodeURIComponent(newRole) + '&locationId=' + props.location.state.locationId, {
            mode: "cors",
            method: 'PUT',
            cache: "no-cache",
            headers: { "Authorization": "Bearer " + token }
        })
            .then(function (response) {
                if (!response.ok) {
                    throw new Error('Network error');
                }
                return response.text();
            })
            .then((result) => {
                // Result contains "Ok"
                // Once successful, update the users to reflect removal of the user:
                setUsers(prevState => {
                    return {
                        ...prevState,
                        [user.id]: {
                            ...prevState[user.id],
                            authorization: newRole
                        }
                    };
                });
                hideLoading();
            })
            .catch(function (error) {
                // Set visual cue and error text to be displayed on registration screen
                console.error("Cannot update user role for location...");
                console.error(error);
                showMessage({ newMessage: 'Network error.' });
                hideLoading();
            });
    }

    function removeUser(user) {
        if (!window.confirm('Are you sure you want to remove user ' + user.email + '?'))
            return;

        // Ask server to remove user from this location:
        showLoading();
        fetch(apiHostName + '/v1/locations/' + props.location.state.locationId + '/' + encodeURIComponent(user.id), {
            mode: "cors",
            method: 'DELETE',
            cache: "no-cache",
            headers: { "Authorization": "Bearer " + token }
        })
            .then(function (response) {
                if (!response.ok) {
                    throw new Error('Network error');
                }
                return response.text();
            })
            .then((result) => {
                // Result contains "Ok"
                // Once successful, update the locations to reflect removal of the user:
                setUsers(prevState => {
                    const { [user.id]: _, ...users } = prevState;
                    return users;
                });
                hideLoading();
            })
            .catch(function (error) {
                // Set visual cue and error text to be displayed on registration screen
                console.error("Cannot delete user for location...");
                console.error(error);
                showMessage({ newMessage: 'Network error.' });
                hideLoading();
            });
    }

    function changeInvitation(invitation) {

        const newRole = invitation.authorization === 'Operate' ? 'Manage' : 'Operate';
        if (!window.confirm('Are you sure you want to change the role for user ' + invitation.email + '?\nThe new role will be: ' + newRole))
            return;

        // change authorization on server
        showLoading();
        fetch(apiHostName + '/v1/invitations/' + invitation.id + '/role?role=' + encodeURIComponent(newRole), {
            mode: "cors",
            method: 'PUT',
            cache: "no-cache",
            headers: { "Authorization": "Bearer " + token }
        })
            .then(function (response) {
                if (!response.ok) {
                    throw new Error('Network error');
                }
                return response.json();
            })
            .then((result) => {
                // Result contains "Ok"
                // Once successful, update the invitations to reflect update of the user's role:
                setInvitations(prevState => {
                    return {
                        ...prevState,
                        [invitation.id]: {
                            ...prevState[invitation.id],
                            authorization: newRole
                        }
                    };
                });
                hideLoading();
            })
            .catch(function (error) {
                // Set visual cue and error text to be displayed on registration screen
                console.error("Cannot update user role for location...");
                console.error(error);
                showMessage({ newMessage: 'Network error.' });
                hideLoading();
            });
    }

    function removeInvitation(invitation) {
        if (!window.confirm('Are you sure you want to remove the invitation for user ' + invitation.email + '?'))
            return;

        // Ask server to remove invitation from this location:
        showLoading();
        fetch(apiHostName + '/v1/invitations/' + encodeURIComponent(invitation.id), {
            mode: "cors",
            method: 'DELETE',
            cache: "no-cache",
            headers: { "Authorization": "Bearer " + token }
        })
            .then(function (response) {
                if (!response.ok) {
                    throw new Error('Network error');
                }
                return response.text();
            })
            .then((result) => {
                // Result contains "Ok"
                // Once successful, update the invitations to reflect removal of the user:
                setInvitations(prevState => {
                    const { [invitation.id]: _, ...invitations } = prevState;
                    return invitations;
                });
                hideLoading();
            })
            .catch(function (error) {
                // Set visual cue and error text to be displayed on registration screen
                console.error("Cannot delete invitation for location...");
                console.error(error);
                showMessage({ newMessage: 'Network error.' });
                hideLoading();
            });


    }

    function shareInvitation(invitation) {
        // Share invitation - either by using the share functionality, or by popping up the invitation url, depending on the capabilities of the browser:
        if (window.navigator.share) {
            // We can use the share API:
            navigator.share({
                title: 'Invitation to enjoy EnjoyableWarmth',
                text: 'You have been invited to use EnjoyableWarmth devices. Please visit this link to accept the invitation, which is valid until ' + invitation.expires.toString() + '.',
                url: 'https://app.enjoyablewarmth.com/invitation?id=' + encodeURIComponent(invitation.id)
            });
        }
        else {
            setSelectedInvitationUrl('https://app.enjoyablewarmth.com/invitation?id=' + encodeURIComponent(invitation.id));
            setSelectedEmail(invitation.email);
            setShowShareDialog(true);
        }
    }

    return (
        !props.location || !props.location.state || !props.location.state.locationId ?
            <Redirect to='/locations' />
            : <ThemeContext.Consumer>
                {(themeContext) => (
                    <Card bg={themeContext.theme} text={themeContext.textColor}>
                        <Card.Body>
                            <Card.Title  >
                                Manage Users
                            <Button variant='secondary' title="Invite new user" style={{ marginLeft: '30px' }} onClick={() => setShowDialog(!showDialog)} >
                                    <FontAwesomeIcon icon="plus-circle" />
                                </Button>
                            </Card.Title>
                            <Card.Subtitle>
                                Add, remove or update users for location {locations[props.location.state.locationId].name}
                            </Card.Subtitle>
                            <Card.Text as="div" style={{ paddingTop: '2em' }}>
                                <div>
                                    Roles: <FontAwesomeIcon icon="cog" /> Operate &nbsp;&nbsp;&nbsp; <FontAwesomeIcon icon="cogs" /> Manage
                            </div>
                                {Object.values(users).length <= 1 && <div style={{ paddingTop: '12px', paddingBottom: '12px' }}><FontAwesomeIcon icon="user" /> No authorized users. <Spinner /></div>}
                                {Object.values(users).length > 1 &&
                                    <Table variant={themeContext.theme} size="sm" style={{ tableLayout: 'fixed' }} >
                                        <thead>
                                            <tr>
                                                <th>E-mail</th>
                                                <th width='40px' style={{ paddingLeft: '3px', paddingRight: '3px' }}>Role</th>
                                                <th width='82px' style={{ whiteSpace: 'nowrap', paddingLeft: '3px', paddingRight: '3px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.values(users).map(user => (
                                                user.authorization !== 'Owner' &&
                                                //Render a table with e-mail, role (icon), remove button, change role button
                                                <tr key={user.email}>
                                                    <td style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }} title={user.email}>{user.email}</td>
                                                    <td>
                                                        {user.authorization === 'Operate' && <FontAwesomeIcon title='Operate' icon="cog" />}
                                                        {user.authorization === 'Manage' && <FontAwesomeIcon title='Manage' icon="cogs" />}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap', paddingRight: '3px' }}>
                                                        <Button variant='secondary' onClick={e => changeRole(user)} title='Change role' style={{ paddingLeft: '8px', paddingRight: '8px', paddingTop: '5px', paddingBottom: '5px' }}><FontAwesomeIcon icon="sync" /></Button>
                                                        <Button variant='secondary' onClick={e => removeUser(user)} style={{ marginLeft: '5px', paddingLeft: '8px', paddingRight: '8px', paddingTop: '5px', paddingBottom: '5px' }} title='Remove user'><FontAwesomeIcon icon="minus-circle" /></Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                }

                                <UserInvitationDialog show={showDialog} onHide={() => setShowDialog(false)} location={locations[props.location.state.locationId]} invitations={invitations} setInvitations={setInvitations} users={users} />
                                <ShareInvitationDialog show={showShareDialog} onHide={() => setShowShareDialog(false)} targetEmail={selectedEmail} invitationUrl={selectedInvitationUrl} />

                                {Object.values(invitations).length === 0 && <div><FontAwesomeIcon icon="envelope" /> No pending invitations. <Spinner /></div>}
                                {Object.values(invitations).length > 0 &&
                                    <React.Fragment>
                                        <div><FontAwesomeIcon icon="envelope" /> Pending invitations:</div>
                                        <Table variant={themeContext.theme} size="sm" style={{ tableLayout: 'fixed' }} >
                                            <thead>
                                                <tr>
                                                    <th>E-mail</th>
                                                    <th width='40px' style={{ paddingLeft: '3px', paddingRight: '3px' }}>Role</th>
                                                    <th width='129px' style={{ whiteSpace: 'nowrap', paddingLeft: '3px', paddingRight: '3px' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.values(invitations).map(invitation => (

                                                    //Render a table with e-mail, role (icon), remove button, change role button
                                                    <tr key={invitation.email}>
                                                        <td style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }} title={invitation.email}>{invitation.email}</td>
                                                        <td>
                                                            {invitation.authorization === 'Operate' && <FontAwesomeIcon title='Operate' icon="cog" />}
                                                            {invitation.authorization === 'Manage' && <FontAwesomeIcon title='Manage' icon="cogs" />}
                                                        </td>
                                                        <td style={{ whiteSpace: 'nowrap', paddingRight: '3px' }}>
                                                            <Button variant='secondary' onClick={e => shareInvitation(invitation)} title='Share invitation' style={{ paddingLeft: '8px', paddingRight: '8px', paddingTop: '5px', paddingBottom: '5px' }}><FontAwesomeIcon icon="share-alt" /></Button>
                                                            <Button variant='secondary' onClick={e => changeInvitation(invitation)} title='Change role' style={{ marginLeft: '5px', paddingLeft: '8px', paddingRight: '8px', paddingTop: '5px', paddingBottom: '5px' }}><FontAwesomeIcon icon="sync" /></Button>
                                                            <Button variant='secondary' onClick={e => removeInvitation(invitation)} style={{ marginLeft: '5px', paddingLeft: '8px', paddingRight: '8px', paddingTop: '5px', paddingBottom: '5px' }} title='Remove invitation'><FontAwesomeIcon icon="minus-circle" /></Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </React.Fragment>
                                }
                                </Card.Text>
                        </Card.Body>
                    </Card>

                )}
            </ThemeContext.Consumer>
    );
}


export default ManageUsers;



// WEBPACK FOOTER //
// ./src/components/ManageUsers.js